import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  formaterDate,
  formaterMontantDevise,
  formaterPoints,
  formaterPointsCumules,
} from '@gsm/partage';
import { useRessource, messageErreur } from '../api/ressource';
import { api, ErreurApi } from '../api/client';
import { useAuth } from '../auth/contexte';
import { Bloc, Tableau, type Colonne } from '../composants/Tableau';
import { BandeauAlerte } from '../composants/BandeauAlerte';
import { Note } from '../composants/Note';
import { Pastille, type TonPastille } from '../composants/Pastille';
import { LienFiche } from '../composants/LienAction';
import { BarreFiltres, FiltreChoix } from '../composants/Filtres';

interface Campagne {
  id: string;
  reference: string;
  origine: 'CHANGEMENT_ALLOCATION' | 'REBALANCING' | 'INVESTISSEMENT';
  statut: 'OUVERTE' | 'VALIDEE' | 'ANNULEE';
  dateReference: string;
  creeLe: string;
  profil: { code: string; libelle: string } | null;
  _count: { mandats: number };
}

/**
 * Un mandat actif, tel que le rend `GET /campagnes/alignement` : sa dérive à
 * la dernière valorisation et, s'il y a lieu, pourquoi il est à réaligner.
 */
interface LigneAlignement {
  mandatId: string;
  reference: string;
  client: string;
  profilId: string;
  profil: string;
  motif: 'CHANGEMENT_PROFIL' | 'CHANGEMENT_ALLOCATION' | 'HORS_CIBLE' | 'DERIVE' | null;
  depuis: string | null;
  versionAllocation: number | null;
  /** Positions qu'aucune ligne de la cible n'accueille : le moteur les céderait. */
  horsCible: { libelle: string; valeur: string }[];
  enDerive: boolean;
  /**
   * Dérive ligne par ligne : somme des écarts absolus de chaque ligne de
   * l'allocation (et de chaque position hors cible), en points.
   */
  derive: {
    total: string;
    seuil: string;
    lignes: { libelle: string; horsCible: boolean; ecart: string }[];
    dateValo: string;
  } | null;
}

function libelleMotif(l: LigneAlignement): string {
  switch (l.motif) {
    case 'CHANGEMENT_PROFIL':
      return 'Changement de profil';
    case 'CHANGEMENT_ALLOCATION':
      return `Allocation v${l.versionAllocation ?? '—'} en vigueur`;
    case 'HORS_CIBLE':
      return l.horsCible.length === 1
        ? `Hors cible : ${l.horsCible[0]!.libelle}`
        : `Hors cible : ${l.horsCible.length} positions`;
    case 'DERIVE':
      return 'Dérive des cours';
    default:
      return '—';
  }
}

export const TON_CAMPAGNE: Record<Campagne['statut'], TonPastille> = {
  OUVERTE: 'attente',
  VALIDEE: 'succes',
  ANNULEE: 'neutre',
};

export const LIBELLE_CAMPAGNE: Record<Campagne['statut'], string> = {
  OUVERTE: 'Ouverte',
  VALIDEE: 'Validée',
  ANNULEE: 'Annulée',
};

export const LIBELLE_ORIGINE: Record<Campagne['origine'], string> = {
  CHANGEMENT_ALLOCATION: 'Changement d’allocation',
  REBALANCING: 'Rebalancing',
  INVESTISSEMENT: 'Investissement',
};

/**
 * Campagnes et lancement d'un rebalancing.
 *
 * La sélection suit la logique de la file « à investir » : filtre par profil,
 * cases à cocher, un geste pour la sélection. Les mandats à réaligner
 * remontent en tête ; le filtre de profil est une vue, pas une seconde règle.
 */
export function Campagnes() {
  const { aRole } = useAuth();
  const naviguer = useNavigate();
  const { donnees, chargement, erreur } = useRessource<Campagne[]>('/campagnes');
  const alignement = useRessource<LigneAlignement[]>('/campagnes/alignement');
  const campagnes = donnees ?? [];
  const [statutCampagne, setStatutCampagne] = useState('');
  const [origine, setOrigine] = useState('');
  const filtresCampagnes = statutCampagne !== '' || origine !== '';
  const campagnesVisibles = campagnes.filter(
    (c) => (!statutCampagne || c.statut === statutCampagne) && (!origine || c.origine === origine),
  );
  const peutLancer = aRole('GERANT', 'RESP_GESTION', 'ADMIN');

  const [profil, setProfil] = useState('');
  const [seulementSignales, setSeulementSignales] = useState(false);
  const [selection, setSelection] = useState<Set<string>>(new Set());
  const [commentaire, setCommentaire] = useState('');
  const [occupe, setOccupe] = useState(false);
  const [erreurAction, setErreurAction] = useState<string | null>(null);

  // Les mandats à réaligner en tête, puis par référence.
  const toutes = [...(alignement.donnees ?? [])].sort(
    (a, b) =>
      Number(b.motif !== null) - Number(a.motif !== null) || a.reference.localeCompare(b.reference),
  );
  const nbSignales = toutes.filter((l) => l.motif).length;

  const profils = [...new Map(toutes.map((l) => [l.profilId, l.profil])).entries()].sort((a, b) =>
    a[1].localeCompare(b[1], 'fr'),
  );
  const lignes = toutes.filter(
    (l) => (!profil || l.profilId === profil) && (!seulementSignales || l.motif),
  );

  // Seuls les mandats visibles comptent : changer de filtre ne doit pas
  // laisser dans la campagne des mandats que le gérant ne voit plus.
  const choisis = lignes.filter((l) => selection.has(l.mandatId));
  const toutSelectionne = lignes.length > 0 && choisis.length === lignes.length;
  const partiellement = choisis.length > 0 && !toutSelectionne;

  function basculer(id: string) {
    const copie = new Set(selection);
    if (copie.has(id)) copie.delete(id);
    else copie.add(id);
    setSelection(copie);
  }

  function basculerTout() {
    setSelection(toutSelectionne ? new Set() : new Set(lignes.map((l) => l.mandatId)));
  }

  function filtrer(valeurProfil: string, signales: boolean) {
    setSelection(new Set());
    setProfil(valeurProfil);
    setSeulementSignales(signales);
  }

  async function lancer() {
    setErreurAction(null);
    setOccupe(true);
    try {
      const c = await api.post<{ id: string }>('/campagnes/rebalancing', {
        mandatIds: choisis.map((l) => l.mandatId),
        commentaire: commentaire.trim() || undefined,
      });
      naviguer(`/campagnes/${c.id}`);
    } catch (e) {
      setErreurAction(e instanceof ErreurApi ? messageErreur(e) : 'Rebalancing impossible.');
      setOccupe(false);
    }
  }

  const colonnesMandats: Colonne<LigneAlignement>[] = [
    ...(peutLancer
      ? [
          {
            cle: 'choix',
            entete: (
              <input
                type="checkbox"
                checked={toutSelectionne}
                // L'état indéterminé ne s'exprime pas en JSX : il se pose sur le nœud.
                ref={(noeud) => {
                  if (noeud) noeud.indeterminate = partiellement;
                }}
                onChange={basculerTout}
                disabled={lignes.length === 0}
                aria-label="Tout sélectionner"
              />
            ),
            rendu: (l: LigneAlignement) => (
              <input
                type="checkbox"
                checked={selection.has(l.mandatId)}
                onChange={() => basculer(l.mandatId)}
                aria-label={`Sélectionner ${l.reference}`}
              />
            ),
          },
        ]
      : []),
    {
      cle: 'reference',
      entete: 'Mandat',
      num: true,
      rendu: (l) => <LienFiche to={`/mandats/${l.mandatId}`}>{l.reference}</LienFiche>,
    },
    { cle: 'client', entete: 'Client', rendu: (l) => l.client },
    { cle: 'profil', entete: 'Profil', rendu: (l) => l.profil },
    {
      cle: 'derive',
      entete: 'Dérive',
      num: true,
      rendu: (l) => {
        if (!l.derive) return '—';
        const d = l.derive;
        // Les cinq plus forts écarts au survol : de quoi savoir où agir.
        const principaux = d.lignes
          .filter((e) => Number(e.ecart) !== 0)
          .slice(0, 5)
          .map(
            (e) => `${e.libelle}${e.horsCible ? ' (hors cible)' : ''} ${formaterPoints(e.ecart)}`,
          );
        const detail =
          (principaux.length > 0 ? principaux.join(' · ') : 'Aucun écart') +
          ` — seuil ${formaterPointsCumules(d.seuil)}, valorisation du ${formaterDate(d.dateValo)}`;
        const texte = formaterPointsCumules(d.total);
        return (
          <span title={detail}>
            {l.enDerive ? <Pastille ton="attente">{texte}</Pastille> : texte}
          </span>
        );
      },
    },
    {
      cle: 'motif',
      entete: 'À réaligner',
      rendu: (l) =>
        l.horsCible.length > 0 ? (
          <span
            title={`Hors de la cible, cédées en totalité par une campagne : ${l.horsCible
              .map((p) => `${p.libelle} (${formaterMontantDevise(p.valeur)})`)
              .join(', ')}`}
          >
            {libelleMotif(l)}
          </span>
        ) : (
          libelleMotif(l)
        ),
    },
    {
      cle: 'depuis',
      entete: 'Depuis',
      num: true,
      rendu: (l) => (l.depuis ? formaterDate(l.depuis) : '—'),
    },
  ];

  const colonnes: Colonne<Campagne>[] = [
    {
      cle: 'reference',
      entete: 'Référence',
      num: true,
      rendu: (c) => <LienFiche to={`/campagnes/${c.id}`}>{c.reference}</LienFiche>,
    },
    { cle: 'origine', entete: 'Origine', rendu: (c) => LIBELLE_ORIGINE[c.origine] },
    { cle: 'profil', entete: 'Profil', rendu: (c) => c.profil?.libelle ?? '—' },
    { cle: 'mandats', entete: 'Mandats', num: true, rendu: (c) => c._count.mandats },
    { cle: 'date', entete: 'Date', num: true, rendu: (c) => formaterDate(c.dateReference) },
    {
      cle: 'statut',
      entete: 'Statut',
      rendu: (c) => <Pastille ton={TON_CAMPAGNE[c.statut]}>{LIBELLE_CAMPAGNE[c.statut]}</Pastille>,
    },
  ];

  return (
    <>
      <p className="entete__fil">Gestion</p>
      <div className="entete">
        <h1>Campagnes de réajustement</h1>
        <span className="entete__ref">{campagnes.length} campagnes</span>
      </div>

      {erreur && (
        <BandeauAlerte nature="refus" titre="Chargement impossible">
          {messageErreur(erreur)}
        </BandeauAlerte>
      )}

      {alignement.erreur && (
        <BandeauAlerte nature="refus" titre="Chargement impossible">
          {messageErreur(alignement.erreur)}
        </BandeauAlerte>
      )}
      {erreurAction && (
        <BandeauAlerte nature="refus" titre="Rebalancing refusé">
          {erreurAction}
        </BandeauAlerte>
      )}

      <Bloc
        titre="Rebalancing"
        meta={
          alignement.chargement
            ? 'chargement…'
            : `${nbSignales} à réaligner sur ${toutes.length} mandat(s) actif(s)`
        }
      >
        <BarreFiltres
          actifs={profil !== '' || seulementSignales}
          onRetirer={() => filtrer('', false)}
        >
          <FiltreChoix
            libelle="Profil"
            tous="Tous les profils"
            valeur={profil}
            onChange={(v) => filtrer(v, seulementSignales)}
            options={profils}
          />
          <label className="case-a-cocher">
            <input
              type="checkbox"
              checked={seulementSignales}
              onChange={(e) => filtrer(profil, e.target.checked)}
            />
            À réaligner seulement
          </label>
        </BarreFiltres>

        <Tableau
          colonnes={colonnesMandats}
          lignes={lignes}
          cle={(l) => l.mandatId}
          vide={seulementSignales ? 'Aucun mandat à réaligner.' : 'Aucun mandat actif.'}
        />
        <Note resume="Une campagne applique la cible en vigueur aux mandats cochés ; rien ne la déclenche d’office.">
          Une nouvelle allocation, un changement de profil, une position hors cible ou une dérive
          signalent seulement les mandats à réaligner. Une position est hors cible quand aucune
          ligne de l’allocation ne l’accueille — typiquement un BDT qui, en vieillissant, a quitté
          sa tranche : une campagne la céderait en totalité. La dérive additionne, à la dernière
          valorisation, les écarts absolus entre le poids de chaque ligne de l’allocation dans
          l’actif net et sa cible ; elle est signalée au-delà du seuil (paramètre SEUIL_DERIVE_PTS,
          10 points par défaut). Le détail des écarts s’affiche au survol.
        </Note>

        {peutLancer && (
          <div className="barre-actions barre-actions--apres barre-actions--champs">
            <div className="champ champ--large">
              <label className="surtitre" htmlFor="commentaire">
                Commentaire
              </label>
              <input
                id="commentaire"
                value={commentaire}
                maxLength={300}
                onChange={(e) => setCommentaire(e.target.value)}
                placeholder="Motif du rebalancing (facultatif)"
              />
            </div>
            <button
              className="bouton"
              disabled={occupe || choisis.length === 0}
              onClick={() => void lancer()}
            >
              {occupe ? 'Génération…' : `Rebalancer la sélection (${choisis.length})`}
            </button>
          </div>
        )}
      </Bloc>

      <Bloc
        titre="Campagnes"
        meta={
          chargement
            ? 'chargement…'
            : filtresCampagnes
              ? `${campagnesVisibles.length} sur ${campagnes.length}`
              : undefined
        }
      >
        <BarreFiltres
          actifs={filtresCampagnes}
          onRetirer={() => {
            setStatutCampagne('');
            setOrigine('');
          }}
        >
          <FiltreChoix
            libelle="Statut"
            tous="Tous les statuts"
            valeur={statutCampagne}
            onChange={setStatutCampagne}
            options={Object.entries(LIBELLE_CAMPAGNE)}
          />
          <FiltreChoix
            libelle="Origine"
            tous="Toutes les origines"
            valeur={origine}
            onChange={setOrigine}
            options={Object.entries(LIBELLE_ORIGINE)}
          />
        </BarreFiltres>
        <Tableau
          colonnes={colonnes}
          lignes={campagnesVisibles}
          cle={(c) => c.id}
          vide={filtresCampagnes ? 'Aucune campagne ne répond à ces critères.' : 'Aucune campagne.'}
        />
      </Bloc>
    </>
  );
}
